import { AutocompleteOption } from "components/Form/components/Autocomplete/Autocomplete";
import { debounce } from "lodash";
import { MutableRefObject, useEffect, useMemo, useRef } from "react";
import CreatableSelect from "react-select/creatable";
import "../Autocomplete.scss";

// Per-keystroke search debounce: without it, every character typed into an
// async picker fires one GraphQL request. Menu open/close, clear and
// selection stay immediate — only the typed-search fetch is debounced.
const SEARCH_DEBOUNCE_MS = 250;

interface AsyncSelectProps {
    menuRef: MutableRefObject<any>;
    selectedValue: any;
    dynamicOptions: AutocompleteOption[];
    loadOptions: (inputValue: string) => Promise<any>;
    handleMenuOpen: () => Promise<any>;
    currentPageRef: MutableRefObject<number>;
    setTerm: (term: string) => void;
    isSelectedOptionHidden: boolean;
    uniqueKey: string;
    restProps: any;
}

export const AsyncSelect = ({
    menuRef,
    selectedValue,
    dynamicOptions,
    loadOptions,
    handleMenuOpen,
    currentPageRef,
    setTerm,
    isSelectedOptionHidden,
    uniqueKey,
    ...restProps
}: AsyncSelectProps) => {
    const isClosingStateRef = useRef(false);

    // Keep the debounced searcher stable across renders while always invoking
    // the latest loadOptions closure (which changes identity as its deps move).
    const loadOptionsRef = useRef(loadOptions);
    loadOptionsRef.current = loadOptions;

    const debouncedSearch = useMemo(
        () =>
            debounce((inputValue: string) => {
                loadOptionsRef.current(inputValue);
            }, SEARCH_DEBOUNCE_MS),
        []
    );

    // Cancel any pending search fetch on unmount.
    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    return (
        <CreatableSelect
            ref={menuRef}
            key={uniqueKey}
            options={dynamicOptions}
            value={selectedValue}
            onMenuOpen={handleMenuOpen}
            onInputChange={(newValue, { action }) => {
                if (action === "input-change") {
                    currentPageRef.current = -1;
                    if (newValue === "") {
                        // Clearing the field restores the full list immediately;
                        // drop any queued debounced search.
                        debouncedSearch.cancel();
                        loadOptions("");
                        setTerm("");
                    } else {
                        // Debounce the typed-search fetch (one request per pause,
                        // not per keystroke).
                        debouncedSearch(newValue);
                    }
                } else if (action === "input-blur" || action === "set-value") {
                    // If the input is blurred, we need to clear the term
                    // so the next menu-close will not fetch
                    debouncedSearch.cancel();
                    setTerm("");
                    currentPageRef.current = -1;
                    isClosingStateRef.current = true;
                } else if (action === "menu-close") {
                    debouncedSearch.cancel();
                    if (isClosingStateRef.current) {
                        isClosingStateRef.current = false;
                    } else {
                        currentPageRef.current = -1;
                        loadOptions("");
                        setTerm("");
                    }
                }
            }}
            hideSelectedOptions={isSelectedOptionHidden}
            {...restProps}
        />
    );
};
